import type { AnswerQuestionValue } from "../answer/model";

export type SlaQuestionFilterValue =
  | string
  | string[]
  | Date
  | Date[]
  | number
  | null
  | undefined;

export const hasSlaFilterValue = (value: SlaQuestionFilterValue) =>
  value !== null &&
  value !== undefined &&
  (Array.isArray(value) || typeof value !== "string" || value.trim() !== "") &&
  (!Array.isArray(value) || value.length > 0);

export type SlaFilters = {
  questionFilters: Record<string, SlaQuestionFilterValue>;
  tagFilters: Record<string, SlaQuestionFilterValue>;
  dateRange: Date[] | null;
};

export type SlaQuestionConfigOption = {
  value: string;
  label?: string;
  text?: string;
  previousValues?: string[];
  isDisabled?: boolean;
};

export type SlaQuestion = {
  _id: string;
  text: string;
  dataTitle?: string;
  type: string;
  showInDashboard?: boolean;
  config?: {
    multiline?: boolean;
    options?: SlaQuestionConfigOption[];
  };
};

export type SlaTagOption = {
  id?: string;
  text: string;
  order?: number;
  color?: string;
  isDisabled?: boolean;
  isDeleted?: boolean;
};

export type SlaTag = {
  id: string;
  text: string;
  type: "single" | "multiple" | "text";
  showInDashboard?: boolean;
  options?: SlaTagOption[];
  isDeleted?: boolean;
};

export type SlaQuestionnaire = {
  demographics?: SlaQuestion[];
  indicators?: SlaQuestion[];
  questions?: SlaQuestion[];
};

export const getSlaQuestionnaireQuestions = (
  questionnaire: SlaQuestionnaire | null | undefined,
): SlaQuestion[] => [
  ...(questionnaire?.demographics || []),
  ...(questionnaire?.indicators || []),
  ...(questionnaire?.questions || []),
];

export type SlaSubmissionQuestionValue =
  | SlaQuestionFilterValue
  | AnswerQuestionValue;

export type SlaSubmissionQuestion = {
  questionId: string;
  value: SlaSubmissionQuestionValue;
};

export type SlaSubmissionTag = {
  tagId: string;
  value: SlaQuestionFilterValue;
};

export type SlaSubmission = {
  id: string;
  createdAt: string | Date;
  questions: SlaSubmissionQuestion[];
  tags: SlaSubmissionTag[];
};

export type SlaDashboardSource = {
  questionnaire?: SlaQuestionnaire;
  fixedQuestions?: string[];
  fixedTags?: string[];
  tags?: SlaTag[];
  surveyId?: string;
};

export type SlaDashboardAnswer = {
  answer: string | number | null | undefined;
  count?: number;
};

export type SlaDashboardMatrixResult = {
  item?: string;
  result: Array<{ answer: number; count: number }>;
};

export type SlaDashboardResult = SlaDashboardAnswer | SlaDashboardMatrixResult;

export type SlaDashboardOption = {
  id?: string;
  value?: string | number;
  text?: string;
  label?: string;
  order?: number;
  isDeleted?: boolean;
  isDisabled?: boolean;
};

export type SlaDashboardConfig = {
  options?: SlaDashboardOption[];
  items?: SlaDashboardOption[];
  minValue?: string | number;
  maxValue?: string | number;
  dashboardOptionOrder?: "registered" | "countAscending" | "countDescending";
  minText?: string;
  maxText?: string;
};

export type SlaDashboardQuestion = {
  id?: string;
  text?: string;
  dataTitle?: string;
  dataDescription?: string;
  type: string;
  showInDashboard?: boolean;
  result?: SlaDashboardResult[];
  filteredResult?: SlaDashboardResult[];
  config?: SlaDashboardConfig;
};

export type SlaDashboardTag = SlaDashboardQuestion & {
  id: string;
  text: string;
  options?: SlaDashboardOption[];
};

export type SlaDashboardData = {
  demographics?: SlaDashboardQuestion[];
  indicators?: SlaDashboardQuestion[];
  questions?: SlaDashboardQuestion[];
  tags?: SlaDashboardTag[];
};

export type ProjectSlaState = {
  eligibility:
    | "missingPrerequisites"
    | "available"
    | "configuredHere"
    | "configuredElsewhere";
  sla?: SlaDashboardSource | null;
  blockingSla?: {
    id: string;
    companyId: string;
    surveyId: string;
    projectId?: string;
    projectName?: string;
  } | null;
};

export type SlaAnswerTagChange = {
  answerId: string;
  tagId: string;
  value: SlaQuestionFilterValue;
};
